import { Moment } from 'moment';
import {
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdTransitNowNextPlanet } from '../transit.models';

/**
 * Pure sorteer-helpers voor het Nu & Straks-panel (spec §4). Los van de
 * component gehouden zodat de logica geïsoleerd testbaar blijft.
 */

export type HdTransitNowNextSortMode = 'planetOrder' | 'nextShift';

/**
 * Default sorteermode (finetune Steven ronde 2): de standaard
 * planeetvolgorde (`HD_PLANET_ORDER_STANDARD` — maan onder de nodes).
 */
export const HD_TRANSIT_NOW_NEXT_DEFAULT_SORT_MODE: HdTransitNowNextSortMode = 'planetOrder';

/**
 * Index in de standaard planeetvolgorde (`HD_PLANET_ORDER_STANDARD`).
 * Chiron staat daar niet in en valt zo samen met onbekende keys achteraan
 * (besluit 4/9: extensie).
 */
const PLANET_ORDER_INDEX: ReadonlyMap<HdPlanetKey, number> = new Map(
  HD_PLANET_ORDER_STANDARD.map((key, index) => [key, index]),
);

/** Standaard planeetvolgorde; planeten zonder bekende key (null) komen achteraan. */
function planetOrderIndex(planetKey: HdPlanetKey | null): number {
  if (planetKey == null) return Number.MAX_SAFE_INTEGER;
  return PLANET_ORDER_INDEX.get(planetKey) ?? Number.MAX_SAFE_INTEGER;
}

/**
 * Vroegste eerstvolgende overgang van een planeet (lijn- of poort-shift),
 * `null` als beide ontbreken.
 */
export function nextShiftAtUtc(planet: HdTransitNowNextPlanet): Moment | null {
  const nextLine = planet.nextLine?.atUtc ?? null;
  const nextGate = planet.nextGate?.atUtc ?? null;
  if (nextLine == null) return nextGate;
  if (nextGate == null) return nextLine;
  return nextLine.isBefore(nextGate) ? nextLine : nextGate;
}

/**
 * Sorteert de now-next-planeten voor weergave. Het invoer-array wordt niet
 * gemuteerd.
 *
 * - `planetOrder` (default): de standaard planeetvolgorde
 *   (`HD_PLANET_ORDER_STANDARD` — maan onder de nodes); Chiron en onbekende
 *   keys komen achteraan.
 * - `nextShift`: oplopend op eerstvolgende shift; planeten zonder next-shift
 *   komen achteraan. Bij gelijke shift-tijden (of beide afwezig) beslist de
 *   planeetvolgorde zodat de volgorde stabiel en voorspelbaar blijft.
 */
export function sortNowNextPlanets(
  planets: readonly HdTransitNowNextPlanet[],
  mode: HdTransitNowNextSortMode,
): HdTransitNowNextPlanet[] {
  const sorted = [...planets];
  if (mode === 'planetOrder') {
    sorted.sort((a, b) => planetOrderIndex(a.planetKey) - planetOrderIndex(b.planetKey));
    return sorted;
  }
  sorted.sort((a, b) => {
    const shiftA = nextShiftAtUtc(a);
    const shiftB = nextShiftAtUtc(b);
    if (shiftA == null && shiftB == null) {
      return planetOrderIndex(a.planetKey) - planetOrderIndex(b.planetKey);
    }
    if (shiftA == null) return 1;
    if (shiftB == null) return -1;
    const diff = shiftA.valueOf() - shiftB.valueOf();
    return diff !== 0 ? diff : planetOrderIndex(a.planetKey) - planetOrderIndex(b.planetKey);
  });
  return sorted;
}
